<template>
  <div class="help-bubble-root">
    <!-- Trigger lives in the app header (AppLayout); this component owns the panel. -->
    <!-- Slide-over panel -->
    <transition name="help-fade">
      <div v-if="open" class="help-overlay" @keydown.esc="close">
        <div class="help-backdrop" @click="close"></div>
        <transition name="help-slide" appear>
          <aside ref="panel" tabindex="-1" role="dialog" aria-label="Aide de cette page" class="help-panel">
            <header class="help-header">
              <div class="help-header-title">
                <span class="help-header-icon"><v-icon dark size="18">{{ 'mdi-' + (entry ? entry.icon : 'help-circle-outline') }}</v-icon></span>
                <div class="help-header-text">
                  <p class="help-eyebrow">Aide de cette page</p>
                  <h2 class="help-title">{{ entry ? entry.title : 'Aide' }}</h2>
                </div>
              </div>
              <button type="button" class="help-close" aria-label="Fermer" @click="close"><v-icon size="20">mdi-close</v-icon></button>
            </header>

            <div class="help-body">
              <template v-if="entry">
                <p class="help-summary">{{ entry.summary }}</p>

                <button v-if="hasTour" type="button" class="help-tour-btn" @click="startTourNow">
                  <v-icon dark size="16">mdi-star-four-points</v-icon> Démarrer la visite guidée
                </button>

                <section v-if="entry.steps && entry.steps.length" class="help-section">
                  <p class="help-eyebrow help-section-title">Comment faire</p>
                  <ol class="help-steps">
                    <li v-for="(s, i) in entry.steps" :key="i" class="help-step">
                      <span class="help-step-num">{{ i + 1 }}</span>
                      <div>
                        <p class="help-step-title">{{ s.title }}</p>
                        <p class="help-step-body">{{ s.body }}</p>
                      </div>
                    </li>
                  </ol>
                </section>

                <section v-if="entry.faqs && entry.faqs.length" class="help-section">
                  <p class="help-eyebrow help-section-title">Questions fréquentes</p>
                  <div class="help-faqs">
                    <div v-for="(f, i) in entry.faqs" :key="i" class="help-faq">
                      <button type="button" class="help-faq-q" :aria-expanded="openFaq === i" @click="openFaq = openFaq === i ? null : i">
                        <span>{{ f.q }}</span>
                        <v-icon size="18" class="help-faq-chev">{{ openFaq === i ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      </button>
                      <p v-show="openFaq === i" class="help-faq-a">{{ f.a }}</p>
                    </div>
                  </div>
                </section>
              </template>
              <p v-else class="help-empty">Aucune aide spécifique pour cette page. Consultez le centre d'aide.</p>
            </div>

            <footer class="help-footer">
              <button type="button" class="help-all-btn" @click="goToCenter">
                <v-icon size="16">mdi-help-box-multiple-outline</v-icon> Voir tout le centre d'aide
              </button>
            </footer>
          </aside>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { helpFor } from '@/help/helpContent'
import { startTour } from '@/utils/guidedTour'

export default {
  name: 'HelpBubble',
  data() {
    return { open: false, openFaq: null }
  },
  computed: {
    entry() { return helpFor(this.$route && this.$route.name) },
    hasTour() { return !!(this.entry && this.entry.tour && this.entry.tour.length) },
  },
  watch: {
    $route() { this.open = false; this.openFaq = null },
  },
  methods: {
    toggle() {
      this.open = !this.open
      this.openFaq = null
      if (this.open) this.$nextTick(() => { if (this.$refs.panel) this.$refs.panel.focus() })
    },
    close() { this.open = false },
    startTourNow() {
      if (!this.hasTour) return
      this.close()
      this.$nextTick(() => startTour(this.entry.tour))
    },
    goToCenter() {
      this.close()
      if (this.$route.name !== 'help-center') this.$router.push({ name: 'help-center' }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.help-overlay { position: fixed; inset: 0; z-index: 250; }
.help-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.4); }
.help-panel {
  position: absolute; top: 0; right: 0; height: 100%; width: 100%; max-width: 420px;
  background: #fff; border-left: 1px solid #e6e8f0; box-shadow: -20px 0 60px -25px rgba(0,0,0,.4);
  display: flex; flex-direction: column; outline: none;
}
.help-header { display: flex; align-items: center; justify-content: space-between; padding: 0 18px; height: 64px; border-bottom: 1px solid #eef0f6; flex-shrink: 0; }
.help-header-title { display: flex; align-items: center; gap: 10px; min-width: 0; }
.help-header-icon { width: 34px; height: 34px; border-radius: 9px; background: linear-gradient(135deg, #6d5ef6, #2d8cff); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.help-eyebrow { font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: #9aa0b8; margin: 0; }
.help-title { font-size: 17px; font-weight: 700; color: #2b2f52; margin: 2px 0 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.help-close { background: none; border: none; cursor: pointer; color: #9aa0b8; }
.help-body { flex: 1; overflow-y: auto; padding: 18px; }
.help-summary { color: #2b2f52; line-height: 1.55; margin: 0 0 16px; }
.help-tour-btn { width: 100%; border: none; cursor: pointer; border-radius: 10px; padding: 11px; margin-bottom: 18px; color: #fff; font-weight: 600; background: linear-gradient(135deg, #6d5ef6, #2d8cff); display: flex; align-items: center; justify-content: center; gap: 8px; }
.help-section { margin-bottom: 20px; }
.help-section-title { margin-bottom: 10px; }
.help-steps { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
.help-step { display: flex; gap: 10px; }
.help-step-num { flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%; background: rgba(109,94,246,.12); color: #6d5ef6; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.help-step-title { font-size: 14px; font-weight: 600; color: #2b2f52; margin: 0; }
.help-step-body { font-size: 13.5px; color: #6b7192; line-height: 1.5; margin: 2px 0 0; }
.help-faqs { display: flex; flex-direction: column; gap: 8px; }
.help-faq { border: 1px solid #eef0f6; border-radius: 10px; overflow: hidden; }
.help-faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 11px 14px; background: #fff; border: none; cursor: pointer; text-align: left; font-size: 14px; font-weight: 600; color: #2b2f52; }
.help-faq-chev { color: #9aa0b8; flex-shrink: 0; }
.help-faq-a { padding: 0 14px 12px; font-size: 13.5px; color: #6b7192; line-height: 1.55; margin: 0; }
.help-empty { color: #6b7192; }
.help-footer { border-top: 1px solid #eef0f6; padding: 14px 18px; flex-shrink: 0; }
.help-all-btn { width: 100%; border: 1px solid #d7dbea; background: #fff; cursor: pointer; border-radius: 10px; padding: 10px; color: #2b2f52; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; }

.help-fade-enter-active, .help-fade-leave-active { transition: opacity .2s ease; }
.help-fade-enter, .help-fade-leave-to { opacity: 0; }
.help-slide-enter-active, .help-slide-leave-active { transition: transform .25s ease; }
.help-slide-enter, .help-slide-leave-to { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) {
  .help-fade-enter-active, .help-fade-leave-active, .help-slide-enter-active, .help-slide-leave-active { transition: none; }
}
</style>
